import React, { useRef } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { EffectFade, Autoplay, Lazy, type Swiper as SwiperRef } from 'swiper'
import 'swiper/css'
import 'swiper/css/effect-fade'
import 'swiper/css/lazy'
import { to1200Image } from '~/utils'
import Navigation from './Navigation'
import { MyIcon } from '~/components/ui/MyIcon'
import classNames from 'classnames'
import styles from './index.module.css'
import { Post_ImageList_AiArtwork, RiskControlStatus } from '~/graphqls/types'
import { CDNImage } from '~/components/business/CDNImage'
import { useAtom } from 'jotai'
import { detailSwiperIndexAtom } from '../atom'
import { Button } from 'antd'
import { Delete } from 'wujieai-react-icon'

export interface ImageType {
  url: string
  riskControlStatus?: RiskControlStatus | null
  width?: number
  height?: number
  aiArtwork?: Post_ImageList_AiArtwork | null
}
type IProps = {
  previewImages: ImageType[]
  autoplay?: boolean
  onDelete?: (url: string) => void
  onSetCover?: (index: number) => void
  className?: string
  effect?: any
  modules?: any[]
  lazy?: boolean
  style?: React.CSSProperties
  showSetCoverButton?: boolean
  showAddMulti?: boolean
  editImages?: () => void
  showAiGenTag?: boolean
}

const ImageViewer = ({
  previewImages,
  autoplay = false,
  onDelete,
  onSetCover,
  className,
  effect = 'fade',
  modules = [EffectFade, Autoplay, Lazy],
  lazy = true,
  style,
  showSetCoverButton,
  showAddMulti,
  editImages,
  showAiGenTag = true,
}: IProps) => {
  const [swiperindex] = useAtom(detailSwiperIndexAtom)
  const swiperRef = useRef<SwiperRef>()
  // 显示各个按钮
  const showAction =
    !!showAddMulti ||
    (showSetCoverButton && previewImages.length > 1) ||
    !!onDelete
  return (
    <>
      {showAction && (
        <div className="flex justify-end items-center mb-4">
          {showSetCoverButton &&
            previewImages.length > 1 &&
            (swiperindex == 0 ? (
              <div className="border-2 border-primary bg-primary rounded-full px-4 h-[40px] leading-[36px] text-white text-xs cursor-pointer">
                <span>封面图</span>
              </div>
            ) : (
              <div
                className="border-2 border-neutral-300 rounded-full px-4 h-[40px] leading-[36px] text-neutral-400 text-xs cursor-pointer"
                onClick={() => {
                  onSetCover && swiperRef.current?.slideToLoop(0, 0)
                  onSetCover?.(swiperindex)
                }}
              >
                <span>设为封面</span>
              </div>
            ))}
          {!!showAddMulti && (
            <div
              className="border-2 border-neutral-300 rounded-full px-4 h-[40px] leading-[36px] text-neutral-400 text-xs ml-4 cursor-pointer"
              onClick={() => {
                editImages?.()
              }}
            >
              {previewImages.length > 1 ? '编辑多图' : '添加多图'}
            </div>
          )}
          {previewImages.length > 1 && !!onDelete && (
            <div
              className="w-[40px] h-[40px] border-2 border-neutral-300 rounded-full flex items-center justify-center ml-4 cursor-pointer"
              onClick={() => {
                swiperRef.current?.slideToLoop(0, 0)
                onDelete?.(previewImages[swiperindex]?.url)
              }}
            >
              <Delete size={16} />
            </div>
          )}
        </div>
      )}

      <Swiper
        style={style}
        onSwiper={instance => {
          swiperRef.current = instance
        }}
        className={classNames(
          styles.swiper,
          {
            hasNav: previewImages.length > 1,
            [styles.showAction]: showAction,
          },
          className
        )}
        effect={effect}
        loop={true}
        lazy={lazy}
        autoplay={
          autoplay
            ? {
                delay: 1000,
                // 此处为了兼容下载原图：鼠标上移时中断轮播
                disableOnInteraction: true,
                pauseOnMouseEnter: true,
              }
            : false
        }
        modules={modules}
      >
        {previewImages.map((it, index) => (
          <SwiperSlide key={it.url}>
            <div className="relative h-full images-wrap">
              {lazy ? (
                <img
                  className="swiper-lazy"
                  data-src={to1200Image(it.url)}
                  alt=""
                />
              ) : (
                <img src={to1200Image(it.url)} alt="" />
              )}

              {!!it?.aiArtwork?.extend?.competitionLink &&
                swiperindex === index && (
                  <div className="absolute right-16 top-0">
                    <CDNImage
                      className="w-[102px]"
                      src={
                        !it?.aiArtwork?.extend?.competitionRank
                          ? '/images/plaza/competitions_joined_v2.png'
                          : it?.aiArtwork?.extend?.competitionRank <= 30
                          ? `/images/plaza/competitions_${it?.aiArtwork?.extend?.competitionRank}.png`
                          : ''
                      }
                      alt=""
                    />
                  </div>
                )}
              {it?.riskControlStatus &&
                ![
                  RiskControlStatus.ALLOW_USE,
                  RiskControlStatus.NO_RECORD,
                ].includes(it.riskControlStatus) && (
                  <div className="absolute left-1/2 -translate-x-1/2 bottom-3 bg-error/20 px-4 py-2 rounded-xl text-center text-error text-xs flex items-center">
                    <MyIcon
                      icon="info-circle"
                      className="text-base mr-2"
                    ></MyIcon>
                    <span>该图片涉嫌违规</span>
                    {previewImages?.length > 1 && (
                      <span
                        onClick={() => {
                          onDelete?.(it.url)
                        }}
                      >
                        <MyIcon
                          icon="trash"
                          className="text-base ml-8 cursor-pointer align-middle"
                        ></MyIcon>
                      </span>
                    )}
                  </div>
                )}
            </div>
            {showAiGenTag && (
              <CDNImage
                src="/images/common/ai_gen_tag_v2.png"
                className={classNames(
                  'w-[42px] object-cover absolute right-2 z-10 bottom-2'
                )}
              />
            )}
          </SwiperSlide>
        ))}
        {previewImages.length > 1 && <Navigation images={previewImages} />}
      </Swiper>
    </>
  )
}

export default ImageViewer
